import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { Eye, EyeOff } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

export interface CreatedTenant {
  tenantId: string;
  code: string;
  slug: string;
}

export interface CreateTenantModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (tenant: CreatedTenant) => void;
}

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';
type SlugInvalidReason = 'format' | 'length' | 'reserved';

interface CheckSlugResponse {
  available: boolean;
  reason?: string;
}

const FORM_ID = 'create-tenant-form';

interface FieldState {
  slug: string;
  companyName: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerFirstName: string;
  ownerLastName: string;
  /** `client_server` = baza dedicated na serwerze klienta — tenant powstaje bez bazy, czeka na nią. */
  mode: TenantDbChoice;
}

type TenantDbChoice = 'shared' | 'dedicated' | 'client_server';

function emptyFields(): FieldState {
  return {
    slug: '',
    companyName: '',
    ownerEmail: '',
    ownerPassword: '',
    ownerFirstName: '',
    ownerLastName: '',
    mode: 'shared',
  };
}

export function CreateTenantModal({ open, onClose, onSuccess }: CreateTenantModalProps) {
  const { t } = useTranslation();
  const formId = useId();
  const resolvedFormId = FORM_ID + '-' + formId;

  const { hasPermission } = useAuth();
  const canPickServer = hasPermission('db_servers.manage');

  const [fields, setFields] = useState(emptyFields());
  const [showPassword, setShowPassword] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugInvalidReason, setSlugInvalidReason] = useState<SlugInvalidReason | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const prevOpenRef = useRef(false);

  // Reset form gdy modal otwierany od nowa
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFields(emptyFields());
      setShowPassword(false);
      setSlugStatus('idle');
      setSlugInvalidReason(null);
      setIsSubmitting(false);
      setApiError(null);
      setFieldErrors({});
    }
    prevOpenRef.current = open;
  }, [open]);

  // Slug debounce + check-slug
  useEffect(() => {
    const slug = fields.slug;
    if (!slug) {
      setSlugStatus('idle');
      setSlugInvalidReason(null);
      return;
    }

    if (slug.length < 3 || slug.length > 40) {
      setSlugStatus('invalid');
      setSlugInvalidReason('length');
      return;
    }

    let cancelled = false;
    setSlugStatus('checking');
    setSlugInvalidReason(null);

    const timerId = setTimeout(async () => {
      try {
        const res = await api.post<CheckSlugResponse>('/tenants/check-slug', { slug });
        if (cancelled) return;
        if (res.data.available) {
          setSlugStatus('available');
        } else {
          const reason = res.data.reason;
          if (reason === 'taken') {
            setSlugStatus('taken');
          } else {
            setSlugStatus('invalid');
            setSlugInvalidReason((reason as SlugInvalidReason) ?? 'format');
          }
        }
      } catch {
        if (cancelled) return;
        setSlugStatus('idle');
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [fields.slug]);

  function setField<K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) {
    setFields((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};
    const required = t('admin.tenants.modal.errorFieldRequired');

    if (!fields.slug.trim()) errors.slug = required;
    if (!fields.companyName.trim()) errors.companyName = required;
    if (!fields.ownerEmail.trim() || !fields.ownerEmail.includes('@')) errors.ownerEmail = required;
    if (fields.ownerPassword.length < 10)
      errors.ownerPassword = t('admin.tenants.modal.errorPasswordTooShort');

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    if (slugStatus === 'taken' || slugStatus === 'invalid' || slugStatus === 'checking') return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const res = await api.post<CreatedTenant>('/admin/tenants', {
        slug: fields.slug,
        companyName: fields.companyName,
        ownerEmail: fields.ownerEmail,
        ownerPassword: fields.ownerPassword,
        ownerFirstName: fields.ownerFirstName || null,
        ownerLastName: fields.ownerLastName || null,
        // Serwer klienta: tenant powstaje bez bazy — serwer i bazę podłącza się w jego karcie.
        mode: fields.mode,
      });
      onSuccess(res.data);
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      let msg: string;
      if (code === 'SLUG_TAKEN') {
        setSlugStatus('taken');
        msg = t('admin.tenants.modal.errorSlugTaken');
      } else if (code === 'INVALID_SLUG') {
        msg = t('admin.tenants.modal.errorInvalidSlug');
      } else if (code === 'VALIDATION_FAILED') {
        msg = t('admin.tenants.modal.errorValidation');
      } else if (code === 'NO_POOL_CAPACITY') {
        msg = t('admin.tenants.modal.errorNoPool');
      } else if (code === 'PROVISIONING_FAILED') {
        msg = t('admin.tenants.modal.errorProvisioning');
      } else if (code === 'INVALID_INPUT') {
        msg = t('admin.tenants.modal.errorInvalidInput');
      } else if (code === 'DB_SERVER_UNAVAILABLE') {
        msg = t('admin.tenants.modal.errorDbServerUnavailable');
      } else if (code === 'DB_SERVER_UNREACHABLE') {
        msg = t('admin.tenants.modal.errorDbServerUnreachable');
      } else if (code === 'FORBIDDEN' || axErr.response?.status === 403) {
        msg = t('admin.tenants.modal.errorForbidden');
      } else if (code === 'UNAUTHORIZED' || axErr.response?.status === 401) {
        msg = t('admin.tenants.modal.errorUnauthorized');
      } else {
        msg = t('admin.tenants.modal.errorUnknown');
      }
      setApiError(msg);
      setIsSubmitting(false);
    }
  }

  const slugBadge = (() => {
    if (slugStatus === 'idle') return null;
    if (slugStatus === 'checking')
      return <Badge color="gray">{t('admin.tenants.modal.slugChecking')}</Badge>;
    if (slugStatus === 'available')
      return <Badge color="green">{t('admin.tenants.modal.slugAvailable')}</Badge>;
    if (slugStatus === 'taken')
      return <Badge color="red">{t('admin.tenants.modal.slugTaken')}</Badge>;
    // invalid
    const reasonKey =
      slugInvalidReason === 'length'
        ? 'slugInvalidLength'
        : slugInvalidReason === 'reserved'
          ? 'slugInvalidReserved'
          : 'slugInvalidFormat';
    return <Badge color="amber">{t(`admin.tenants.modal.${reasonKey}`)}</Badge>;
  })();

  const submitDisabled =
    isSubmitting || slugStatus === 'checking' || slugStatus === 'taken' || slugStatus === 'invalid';

  const modeOptions = [
    { value: 'shared', label: t('admin.tenants.modal.modeShared') },
    { value: 'dedicated', label: t('admin.tenants.modal.modeDedicated') },
    // Serwer klienta: tylko dla adminów z dostępem do rejestru serwerów baz.
    ...(canPickServer
      ? [{ value: 'client_server', label: t('admin.tenants.modal.modeClientServer') }]
      : []),
  ];

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.tenants.modal.buttonCancel')}
      </Button>
      <Button type="submit" form={resolvedFormId} loading={isSubmitting} disabled={submitDisabled}>
        {isSubmitting
          ? t('admin.tenants.modal.buttonSubmitting')
          : t('admin.tenants.modal.buttonSubmit')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.tenants.modal.title')}
      description={t('admin.tenants.modal.description')}
      size="lg"
      footer={footer}
      closeOnBackdrop={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <form id={resolvedFormId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {/* Slug */}
        <div className="flex flex-col gap-1">
          <Input
            label={t('admin.tenants.modal.fieldSlug')}
            value={fields.slug}
            onChange={(e) => setField('slug', e.target.value)}
            error={fieldErrors.slug}
            required
            placeholder="np. acme-firma"
            autoComplete="off"
          />
          {slugBadge && <div className="mt-0.5">{slugBadge}</div>}
          {!fieldErrors.slug && (
            <span className="text-xs text-text-muted">
              {t('admin.tenants.modal.fieldSlugHint')}
            </span>
          )}
        </div>

        {/* Nazwa firmy */}
        <Input
          label={t('admin.tenants.modal.fieldCompanyName')}
          value={fields.companyName}
          onChange={(e) => setField('companyName', e.target.value)}
          error={fieldErrors.companyName}
          required
        />

        {/* E-mail */}
        <Input
          label={t('admin.tenants.modal.fieldOwnerEmail')}
          type="email"
          value={fields.ownerEmail}
          onChange={(e) => setField('ownerEmail', e.target.value)}
          error={fieldErrors.ownerEmail}
          required
          autoComplete="off"
        />

        {/* Hasło */}
        <Input
          label={t('admin.tenants.modal.fieldOwnerPassword')}
          type={showPassword ? 'text' : 'password'}
          value={fields.ownerPassword}
          onChange={(e) => setField('ownerPassword', e.target.value)}
          error={fieldErrors.ownerPassword}
          required
          autoComplete="new-password"
          suffixIcon={
            <button
              type="button"
              data-testid="password-toggle"
              aria-label={
                showPassword
                  ? t('admin.tenants.modal.hidePassword')
                  : t('admin.tenants.modal.showPassword')
              }
              onClick={() => setShowPassword((v) => !v)}
              className="pointer-events-auto rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
            >
              {showPassword ? (
                <EyeOff size={16} aria-hidden="true" />
              ) : (
                <Eye size={16} aria-hidden="true" />
              )}
            </button>
          }
        />

        {/* Imię / Nazwisko */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('admin.tenants.modal.fieldOwnerFirstName')}
            value={fields.ownerFirstName}
            onChange={(e) => setField('ownerFirstName', e.target.value)}
          />
          <Input
            label={t('admin.tenants.modal.fieldOwnerLastName')}
            value={fields.ownerLastName}
            onChange={(e) => setField('ownerLastName', e.target.value)}
          />
        </div>

        {/* Tryb DB */}
        <FormField label={t('admin.tenants.modal.fieldMode')}>
          <Dropdown
            value={fields.mode}
            onChange={(v) => setField('mode', v as TenantDbChoice)}
            options={modeOptions}
          />
        </FormField>

        {fields.mode === 'client_server' && (
          <p
            className="rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-700 dark:border-accent-800 dark:bg-accent-900/20 dark:text-accent-300"
            data-testid="create-tenant-client-server-hint"
          >
            {t('admin.tenants.modal.clientServerHint')}
          </p>
        )}

        {/* API error */}
        {apiError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {apiError}
          </div>
        )}
      </form>
    </Modal>
  );
}
